import type { FilterDTO, VehicleTypeDTO } from "../../search";
import type { SearchFilterDTO } from "../../search/dtos/api/search-filters.dto";

export type ReorderFilterTransformOptions = {
  /**
   * When true, keeps each filter's `items` in API order (no sort by `name`).
   * Use for FiltersGrouped, where the backend defines order.
   */
  preserveApiItemOrder?: boolean;
};

export abstract class ReorderFilterTransformer {
  static readonly mappedItems: Record<
    string,
    string | { car: string; bike: string }
  > = {
    SellerType: "Vendedor",
    Cooling: "Tipo de refrigeração",
    Starter: "Tipo de partida",
    Engine: "Tipo de motor",
    OP: "Itens do veículo",
    Shift: "Número de marchas",
    Break: "Freio dianteiro / traseiro",
    Charge: "Tipo de alimentação",
    Transmission: "Câmbio",
    Fuel: "Combustível",
    FinalPlateCarGroupy: "Final da placa",
    Armored: "Blindagem",
    Color: { car: "Cor", bike: "Cor primária" },
    ColorSecundary: "Cor secundária",
    BodyType: { car: "Carroceria", bike: "Estilo" },
    Auction: "Leilão",
    LifeStyle: "Necessidade",
    HotDeal: "Ofertas",
    VA: "Características",
    Category: "Tipo do veículo",
    ListingType: "Novo/Usado",
    State: "Estado",
    Make: "Marca",
    YearModel: "Ano",
    NumberPort: "Portas",
    Media: "Mídia",
    Opportunities: "O que é interessante para você?",
    MostUsed: "Mais utilizados",
    Products: "Produtos Webmotors",
    WebmotorsSpecials: "Especiais Webmotors",
    OfficialStores: "Lojas Oficiais",
  };

  static readonly itemsOrder: string[] = [
    "SellerType",
    "Cooling",
    "Starter",
    "Engine",
    "OP",
    "Shift",
    "Break",
    "Charge",
    "Transmission",
    "Fuel",
    "FinalPlateCarGroupy",
    "Armored",
    "Color",
    "ColorSecundary",
    "BodyType",
    "NumberPort",
    "Auction",
    "LifeStyle",
    "HotDeal",
    "VA",
  ];

  /**
   * Gets the mapped label for a given filter name based on the vehicle type.
   *
   * @param {string} name - The name of the filter.
   * @param {VehicleTypeDTO} vehicleType - The type of vehicle ('carros' or 'motos').
   * @returns {string} - The mapped label as a string with the first word capitalized.
   */
  static getMappedLabel(name: string, vehicleType: VehicleTypeDTO): string {
    const mappedLabel = ReorderFilterTransformer.mappedItems[name];
    const label =
      typeof mappedLabel === "object"
        ? mappedLabel[vehicleType]
        : mappedLabel || name;
    return label;
  }

  /**
   * Transforms the filters array by reordering based on a predefined order and marking listed items.
   *
   * @param {SearchFilterDTO[]} filters - Array of input filters to be transformed.
   * @param {VehicleTypeDTO} vehicleType - The type of vehicle, used to determine labels for some filters.
   * @param {ReorderFilterTransformOptions} [options] - Optional behavior (e.g. preserve API item order).
   * @returns {FilterDTO[]} - Array of transformed filters with the correct order and listed status.
   */
  static transform(
    filters: SearchFilterDTO[],
    vehicleType: VehicleTypeDTO,
    options?: ReorderFilterTransformOptions
  ): FilterDTO[] {
    if (!filters) {
      return [];
    }

    const preserveApiItemOrder = options?.preserveApiItemOrder === true;
    const { getMappedLabel, itemsOrder } = ReorderFilterTransformer;

    const orderedFilters = itemsOrder
      .map((filterName) => filters.find((filter) => filter.name === filterName))
      .filter((filter) => filter !== undefined);

    const remainingFilters = filters.filter(
      (filter) => !itemsOrder.includes(filter.name),
    );

    /**
     * Transforms a single filter by setting its label, listed status, and sorting items alphabetically by label.
     *
     * @param {SearchFilterDTO} filter - The filter to transform.
     * @param {boolean} listed - Boolean indicating if the filter is in the ordered list.
     * @returns {FilterDTO} - Transformed filter with label, listed status, and sorted items.
     */
    const transformFilter = (
      filter: SearchFilterDTO,
      listed: boolean
    ): FilterDTO => {
      const sourceItems = filter?.items?.slice() ?? [];
      const orderedItems = preserveApiItemOrder
        ? sourceItems
        : sourceItems.sort((a, b) => a.name.localeCompare(b.name));

      return {
        label: getMappedLabel(filter.name, vehicleType),
        name: filter.name,
        listed,
        ...(filter.title != null && filter.title !== ""
          ? { title: filter.title }
          : {}),
        items: orderedItems.map((item) => ({
          label: item.name,
          name: item.name,
          count: item.count,
          items: item?.items ?? [],
          ...(item?.description && { description: item.description }),
          ...(item.title != null && item.title !== ""
            ? { title: item.title }
            : {}),
        })),
      };
    };

    return [
      ...orderedFilters.map((filter) => transformFilter(filter, true)),
      ...remainingFilters.map((filter) => transformFilter(filter, false)),
    ];
  }
}
